// Widget loyalty helpers — read-only lookup of a guest's enrolment
// status, points balance, current tier and next-tier nudge. Backed by
// the SECURITY DEFINER RPC `get_widget_loyalty_summary` which scopes
// every read to (tenant_id, lower(email)) so the widget can stay on
// the anon key.

import { supabase } from "../config";

export type LoyaltyTierBenefits = {
  discount_percent?: number;
  discount_cents?: number;
  priority_booking?: boolean;
  birthday_bonus_points?: number;
};

export interface LoyaltySummary {
  ok: true;
  programme_active: boolean;
  programme_name?: string;
  enrolled?: boolean;
  auto_enrol?: boolean;
  point_value_cents?: number;
  current_points_balance?: number;
  lifetime_points_earned?: number;
  lifetime_points_redeemed?: number;
  current_tier?: {
    slug: string;
    name: string;
    benefits: LoyaltyTierBenefits | null;
  } | null;
  next_tier?: {
    slug: string;
    name: string;
    points_to_unlock: number;
  } | null;
}

export type LoyaltyLookupResult =
  | LoyaltySummary
  | { ok: false; reason?: string };

// fetchLoyaltySummary (direct get_widget_loyalty_summary by email) was removed:
// the RPC lost its anon grant in 20260802000030 and every client grant in
// 20260806000080, so the call could only ever return 401 — as it did in
// production on 2026-08-05. The token route below replaced it.

// Loyalty for a token-verified guest. The anon-key lookup above lost its
// grant in 20260802000030 (balances were readable by anyone who knew an email)
// and its client grant entirely in 20260806000080. This is the guest-facing
// route: the server takes the email from the CAPABILITY TOKEN, never from a
// parameter, so a caller can only read the balance they proved they own.
export async function fetchGuestLoyaltyByToken(
  token: string,
): Promise<LoyaltyLookupResult> {
  if (!token) return { ok: false, reason: "invalid_token" };
  const { data, error } = await supabase.rpc("get_guest_loyalty_by_token", {
    p_token: token,
  });
  if (error) return { ok: false, reason: error.message };
  return data as LoyaltyLookupResult;
}

export type RedemptionResult =
  | {
      ok: true;
      points_redeemed: number;
      cents_off: number;
      new_amount_cents: number;
      new_balance: number;
    }
  | { ok: false; reason: string; available?: number };

// Spend points against a single pending booking.
//
// NOT REACHABLE from the widget today, and the call site is disabled behind a
// constant-false guard in CartPanel. redeem_loyalty_points_for_booking resolves
// the guest from a caller-supplied email (so anyone could spend anyone's
// points) and lost every client grant in 20260802000030 / 20260806000080, and
// it carries a documented double-/100 under-credit. Re-enabling it means
// rebuilding it against the capability token — the email must come from the
// TOKEN, exactly as get_guest_loyalty_by_token does — and fixing the
// arithmetic in the same change. Kept so the control still compiles.
export async function redeemLoyaltyPoints(input: {
  bookingId: string;
  email: string;
  points: number;
}): Promise<RedemptionResult> {
  const { data, error } = await supabase.rpc(
    "redeem_loyalty_points_for_booking",
    {
      p_booking_id: input.bookingId,
      p_email: input.email.trim(),
      p_points: input.points,
    },
  );
  if (error) return { ok: false, reason: error.message };
  return data as RedemptionResult;
}
